import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
/* global axios */
import api from '../services/apiService';
import { useParams, useNavigate } from 'react-router-dom';
import he from 'he';
import Select from 'react-select';
import ReportSelectionModal from './ReportSelectionModal';

const normalizeSearchValue = (value) => String(value || '').toLowerCase();

const compact = (values) => values.filter(Boolean).join(' ');

const getCustomerName = (customer) =>
  customer?.company ||
  compact([customer?.firstName, customer?.lastName]) ||
  `Kunde #${customer?.id}`;

const getCustomerMetaParts = (customer) => [
  customer?.companyAddon ? `Zusatz: ${customer.companyAddon}` : null,
  customer?.customerNumber ? `Kd.-Nr.: ${customer.customerNumber}` : null,
  customer?.debtorNumber ? `Deb.-Nr.: ${customer.debtorNumber}` : null,
  customer?.city || null,
].filter(Boolean);

const formatCustomerLabel = (customer) => {
  const name = getCustomerName(customer);
  const meta = getCustomerMetaParts(customer).join(' | ');
  return meta ? `${name} (${meta})` : name;
};

const buildCustomerOption = (customer) => ({
  value: customer.id,
  label: formatCustomerLabel(customer),
  customer,
});

const filterCustomerOption = (option, inputValue) => {
  const query = normalizeSearchValue(inputValue).trim();
  if (!query) return true;

  const customer = option.data.customer || {};
  return [
    customer.company,
    customer.companyAddon,
    customer.customerNumber,
    customer.debtorNumber,
    customer.firstName,
    customer.lastName,
    customer.street,
    customer.postalCode,
    customer.city,
    customer.email,
  ].some((value) => normalizeSearchValue(value).includes(query));
};

const formatCustomerOptionLabel = (option, { context }) => {
  if (context === 'value') return option.label;

  const meta = getCustomerMetaParts(option.customer);

  return (
    <div style={{ lineHeight: 1.25 }}>
      <div>{getCustomerName(option.customer)}</div>
      {meta.length > 0 && (
        <div style={{ color: '#666', fontSize: '12px', marginTop: '2px' }}>
          {meta.join(' | ')}
        </div>
      )}
    </div>
  );
};

const MessageForm = () => {
  const navigate = useNavigate();
  const quillRef = useRef(null);
  const { id } = useParams(); // Falls eine Vorlage direkt über die URL geladen wird

  // State für die Nachricht
  const [editorContent, setEditorContent] = useState('<p><br></p>');
  const [templateName, setTemplateName] = useState('');
  const [subject, setSubject] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [machineTypes, setMachineTypes] = useState([]);
  const [selectedTypePricings, setSelectedTypePricings] = useState([]);
  const [selectedCustomerTypePricings, setSelectedCustomerTypePricings] = useState([]);

  // State für Kunden & Besteller
  const [customers, setCustomers] = useState([]);
  const [customerContacts, setCustomerContacts] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [reportSelection, setReportSelection] = useState(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportPreviewLoading, setReportPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const customerOptions = useMemo(
    () => customers.map(buildCustomerOption),
    [customers]
  );

  const selectedCustomerOption = useMemo(
    () => customerOptions.find(option => String(option.value) === String(selectedCustomer)) || null,
    [customerOptions, selectedCustomer]
  );

  // Lade Nachrichtenvorlagen
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await api.get('/messages/messagetemplates');
        setTemplates(response.data);
      } catch (error) {
        console.error("Fehler beim Laden der Templates:", error);
      }
    };
    fetchTemplates();
  }, []);

  useEffect(() => {
    const fetchMachineTypes = async () => {
      try {
        const response = await api.get('/machine-types');
        const sorted = (response.data || []).sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', 'de', {
            numeric: true,
            ignorePunctuation: true,
            sensitivity: 'base',
          })
        );
        setMachineTypes(sorted);
      } catch (error) {
        console.error('Fehler beim Laden der Maschinentypen:', error);
      }
    };
    fetchMachineTypes();
  }, []);

  // Falls eine ID in der URL vorhanden ist, lade die entsprechende Vorlage
  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    const fetchTemplate = async () => {
      try {
        const response = await api.get(`/messages/messagetemplates/${id}`);
        const { name, subject, bodyHtml } = response.data;

        setTemplateName(name || '');
        setSubject(subject || '');
        setEditorContent(he.decode(bodyHtml || ''));
        setSelectedTemplate(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Fehler beim Laden der Vorlage:", error);
        setLoading(false);
      }
    };
    fetchTemplate();
  }, [id]);

  // Lade Kundenliste
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await api.get('/customers');
        setCustomers(response.data);
      } catch (error) {
        console.error('Fehler beim Laden der Kunden:', error);
      }
    };
    fetchCustomers();
  }, []);

  // Lade Besteller eines Kunden
  useEffect(() => {
    if (!selectedCustomer) {
      setCustomerContacts([]);
      setSelectedContact(null);
      return;
    }

    const fetchContacts = async () => {
      try {
        const response = await api.get(`/contacts/customer/${selectedCustomer}`);
        setCustomerContacts(response.data);
      } catch (error) {
        console.error('Fehler beim Laden der Besteller:', error);
      }
    };
    fetchContacts();
  }, [selectedCustomer]);

  // Auswahl einer Vorlage
  const handleTemplateChange = (selectedOption) => {
    if (!selectedOption) {
      // Wenn keine Vorlage ausgewählt ist, setze die Felder zurück
      setSelectedTemplate(null);
      setTemplateName('');
      setSubject('');
      setEditorContent('<p><br></p>');
      return;
    }
    const template = templates.find(t => t.id === selectedOption.value);
    if (template) {
      setSelectedTemplate(template);
      setTemplateName(template.name);
      setSubject(template.subject);
      setEditorContent(he.decode(template.bodyHtml));
    }
  };

  // Auswahl eines Kunden
  const handleCustomerChange = (selectedOption) => {
    setSelectedCustomer(selectedOption ? selectedOption.value : null);
    setSelectedContact(null);
    setSelectedCustomerTypePricings([]);
  };

  // Auswahl eines Bestellers
  const handleContactChange = (selectedOption) => {
    setSelectedContact(selectedOption ? selectedOption.value : null);
  };

   // Datei-Input ändern
   const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  // E-Mail versenden
  const handleSendEmail = async () => {
    if (!selectedTemplate || !selectedCustomer || !selectedContact) {
      alert("Bitte wählen Sie eine Vorlage, einen Kunden und einen Besteller aus!");
      return;
    }
  
    // Logge den aktuellen Editor-Inhalt
    console.log("DEBUG: Aktueller Editor-Inhalt (editorContent):", editorContent);
  
    // Erstelle ein FormData-Objekt
    const formData = new FormData();
    formData.append("templateId", selectedTemplate.id);
    formData.append("customerId", selectedCustomer);
    formData.append("contactId", selectedContact);
    formData.append("modifiedBodyHtml", editorContent);
  
    // Hänge die Datei an, falls eine ausgewählt wurde
    if (selectedFile) {
      formData.append("attachment", selectedFile);
    }
    if (selectedTypePricings.length > 0) {
      formData.append(
        "typePricingMachineTypeIds",
        JSON.stringify(selectedTypePricings.map(option => option.value))
      );
    }
    if (selectedCustomerTypePricings.length > 0) {
      formData.append(
        "customerTypePricingMachineTypeIds",
        JSON.stringify(selectedCustomerTypePricings.map(option => option.value))
      );
    }
    if (reportSelection) {
      formData.append("reportSelection", JSON.stringify(reportSelection));
    }
  
    console.log("DEBUG: FormData vorbereitet."); // FormData lässt sich nicht direkt per JSON.stringify loggen
  
    try {
      setSending(true);
      const response = await api.post("/messages/send-email", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      console.log("DEBUG: Response vom E-Mail-Versand:", response.data);
      alert("✅ E-Mail erfolgreich versendet!");
    } catch (error) {
      console.error("❌ Fehler beim Senden der E-Mail:", error.response?.data || error.message);
      alert(error.response?.data?.error || "❌ Fehler beim Senden der E-Mail!");
    } finally {
      setSending(false);
    }
  };

  const handlePreviewReport = async () => {
    if (!reportSelection || reportPreviewLoading) return;

    const previewWindow = window.open('', '_blank');
    try {
      setReportPreviewLoading(true);
      const response = await api.post(
        '/messages/report-preview',
        { reportSelection },
        { responseType: 'blob' }
      );
      const previewUrl = URL.createObjectURL(
        new Blob([response.data], { type: 'application/pdf' })
      );
      if (previewWindow) {
        previewWindow.location.href = previewUrl;
      } else {
        window.open(previewUrl, '_blank');
      }
      window.setTimeout(() => URL.revokeObjectURL(previewUrl), 60000);
    } catch (error) {
      if (previewWindow) previewWindow.close();
      console.error('Fehler beim Erstellen der Listen-Vorschau:', error);
      alert('Die PDF-Vorschau konnte nicht erstellt werden.');
    } finally {
      setReportPreviewLoading(false);
    }
  };
  
  

  if (loading) {
    return <div>Daten werden geladen...</div>;
  }

  return (
    <div style={{ maxWidth: '600px', margin: 'auto' }}>
      <h2>Nachricht senden</h2>

      {/* Vorlage auswählen */}
      <Select
        options={templates.map(t => ({ value: t.id, label: t.name }))}
        onChange={handleTemplateChange}
        placeholder="Vorlage wählen"
        isClearable
      />

      {/* Betreff */}
      <input
        type="text"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        placeholder="Betreff"
        disabled
        style={{ width: '100%', marginBottom: '10px' }}
      />

 {/* Datei-Input für den Upload */}
 <div style={{ marginBottom: '10px' }}>
        <label>Datei anhängen: </label>
        <input 
          type="file" 
          onChange={handleFileChange}
        />
      </div>

      <div
        style={{
          marginBottom: '14px',
          padding: '12px',
          border: '1px solid #dbe3ee',
          borderRadius: '8px',
          background: '#f8fafc',
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: '8px', textAlign: 'left' }}>
          Gesamtlisten-PDF
        </div>
        {reportSelection ? (
          <>
            <div
              style={{
                padding: '9px 10px',
                borderRadius: '6px',
                background: '#fff',
                border: '1px solid #cbd5e1',
                textAlign: 'left',
                color: '#334155',
              }}
            >
              📄 {reportSelection.summary}
            </div>
            <div
              style={{
                display: 'flex',
                gap: '8px',
                flexWrap: 'wrap',
                marginTop: '9px',
              }}
            >
              <button
                type="button"
                onClick={handlePreviewReport}
                disabled={reportPreviewLoading}
              >
                {reportPreviewLoading ? 'PDF wird erstellt …' : 'PDF ansehen'}
              </button>
              <button type="button" onClick={() => setReportModalOpen(true)}>
                Auswahl ändern
              </button>
              <button
                type="button"
                onClick={() => setReportSelection(null)}
                style={{ color: '#b91c1c' }}
              >
                Entfernen
              </button>
            </div>
          </>
        ) : (
          <button type="button" onClick={() => setReportModalOpen(true)}>
            Liste als PDF hinzufügen
          </button>
        )}
      </div>

      <div style={{ marginBottom: '10px' }}>
        <label>Typpreislisten anhängen</label>
        <Select
          options={machineTypes.map(type => ({ value: type.id, label: type.name }))}
          value={selectedTypePricings}
          onChange={(options) => setSelectedTypePricings(options || [])}
          placeholder="Typpreislisten wählen"
          isMulti
          isClearable
        />
      </div>

      <div style={{ marginBottom: '10px' }}>
        <label>Kundentyppreislisten anhängen</label>
        <Select
          options={machineTypes.map(type => ({ value: type.id, label: type.name }))}
          value={selectedCustomerTypePricings}
          onChange={(options) => setSelectedCustomerTypePricings(options || [])}
          placeholder={selectedCustomer ? "Kundentyppreislisten wählen" : "Bitte zuerst Kunde wählen"}
          isMulti
          isClearable
          isDisabled={!selectedCustomer}
        />
      </div>

      {/* Kunde auswählen */}
      <Select
        options={customerOptions}
        value={selectedCustomerOption}
        onChange={handleCustomerChange}
        filterOption={filterCustomerOption}
        formatOptionLabel={formatCustomerOptionLabel}
        placeholder="Kunde wählen"
        isClearable
      />

      {/* Besteller auswählen */}
      <Select
        options={customerContacts.map(contact => ({
          value: contact.id,
          label: `${contact.contactFirstName} ${contact.contactLastName} (${contact.contactFunction || '-'})`
        }))}
        onChange={handleContactChange}
        placeholder="Besteller wählen"
        isClearable
        isDisabled={!selectedCustomer}
      />

      {/* E-Mail des Bestellers anzeigen */}
      {selectedContact && (
        <p style={{ marginTop: '5px', fontSize: '14px', color: '#555' }}>
          📧 <strong>{customerContacts.find(c => c.id === selectedContact)?.contactEmail}</strong>
        </p>
      )}

      {/* Nachricht bearbeiten */}
      <ReactQuill
        ref={quillRef}
        value={editorContent}
        onChange={setEditorContent}
        readOnly={false}
        style={{ height: '400px', backgroundColor: '#fff', marginTop: '20px' }}
      />

      {/* E-Mail senden */}
      <button
        onClick={handleSendEmail}
        disabled={sending}
        style={{ marginTop: '60px' }}
      >
        {sending ? 'E-Mail wird gesendet …' : 'E-Mail senden'}
      </button>

      <ReportSelectionModal
        isOpen={reportModalOpen}
        initialSelection={reportSelection}
        onClose={() => setReportModalOpen(false)}
        onApply={setReportSelection}
      />
    </div>
  );
};

export default MessageForm;
